<div class="page">
    <div class="page-head">
        <div>
            <h2>{{ title() }}</h2>
            <p>Every login on the system. Create standalone admin logins — no partner needed.</p>
        </div>
        <p-button label="Add Admin Login" icon="pi pi-user-plus" (onClick)="openCreate()" />
    </div>

    <div class="card">
        <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
                <p-inputicon styleClass="pi pi-search" />
                <input type="text" pInputText placeholder="Search username, email, mobile or role..."
                    [ngModel]="search()" (ngModelChange)="search.set($event)">
            </p-iconfield>
            <span class="count">{{ filtered().length }} of {{ users().length }}</span>
        </div>

        <p-table [value]="filtered()" [loading]="loading()" dataKey="userId" [rows]="10"
            [paginator]="filtered().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
                <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Role</th>
                    <th>Partner</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th class="ta-r">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-u>
                <tr>
                    <td class="name">
                        {{ u.username }}
                        @if (u.userId === myUserId) { <p-tag value="You" severity="info" /> }
                    </td>
                    <td>{{ u.email }}</td>
                    <td class="mono">{{ u.mobileNo }}</td>
                    <td>
                        @if (u.roles) { <p-tag [value]="u.roles" [severity]="isAdminRole(u) ? 'warn' : 'secondary'" /> }
                        @else { <span class="muted">—</span> }
                    </td>
                    <td>
                        @if (u.partnerName) { {{ u.partnerName }} }
                        @else { <span class="muted">— standalone —</span> }
                    </td>
                    <td>{{ u.createdDate | indDate }}</td>
                    <td><p-tag [value]="u.status ? 'ACTIVE' : 'DISABLED'" [severity]="u.status ? 'success' : 'danger'" /></td>
                    <td class="ta-r actions">
                        <p-button icon="pi pi-key" [rounded]="true" [text]="true" severity="secondary"
                            pTooltip="Reset password" (onClick)="openReset(u)" />
                        <p-button [icon]="u.status ? 'pi pi-ban' : 'pi pi-check-circle'" [rounded]="true" [text]="true"
                            [severity]="u.status ? 'danger' : 'success'"
                            [pTooltip]="u.status ? 'Disable login' : 'Enable login'"
                            [disabled]="u.userId === myUserId && u.status"
                            (onClick)="toggleStatus(u)" />
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="8">
                        <div class="state"><i class="pi pi-users"></i>
                            <p>No logins found.</p>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>

<!-- ===================== ADD ADMIN LOGIN ===================== -->
<p-dialog [visible]="showCreate()" (visibleChange)="showCreate.set($event)" [modal]="true" [draggable]="true"
    [resizable]="false" [closable]="true" [dismissableMask]="false" header="Add Admin Login"
    styleClass="menu-dialog" [style]="{ width: '520px' }" [breakpoints]="{ '520px': '94vw' }">
    <form [formGroup]="form" class="menu-form">
        <p class="hint-muted">
            Creates a standalone <strong>Admin</strong> login — full app access, no partner linked, no KYC gate.
        </p>

        <div class="grid-2">
            <div class="field">
                <label>Username <span class="req">*</span></label>
                <input pInputText type="text" formControlName="username" placeholder="e.g. operations" autocomplete="off">
                @if (form.get('username')?.errors?.['taken']) {
                <small class="err">This username is already used by another login</small>
                } @else if (form.get('username')?.touched && form.get('username')?.invalid) {
                <small class="err">At least 3 characters</small>
                }
            </div>
            <div class="field">
                <label>Mobile Number <span class="req">*</span></label>
                <div class="otp-row">
                    <input pInputText type="text" formControlName="mobileNo" placeholder="10-digit mobile" maxlength="10">
                    <p-button [label]="otpSentTo() ? 'Resend OTP' : 'Send OTP'" size="small" [outlined]="true"
                        [loading]="otpSending()" (onClick)="sendOtp()" />
                </div>
                @if (form.get('mobileNo')?.errors?.['taken']) {
                <small class="err">This mobile number is already used by another login</small>
                } @else if (form.get('mobileNo')?.touched && form.get('mobileNo')?.invalid) {
                <small class="err">Valid 10-digit number required</small>
                }
            </div>
        </div>

        @if (otpSentTo()) {
        <div class="field">
            <label>OTP <span class="req">*</span></label>
            <input pInputText type="text" formControlName="otp" placeholder="Code sent to the mobile" maxlength="8"
                autocomplete="one-time-code">
            <small class="hint-muted">Sent to {{ otpSentTo() }} — valid for {{ otpExpiryMin() }} minutes.</small>
            @if (form.get('otp')?.touched && form.get('otp')?.invalid) {
            <small class="err">Enter the code received on the mobile</small>
            }
        </div>
        } @else {
        <small class="hint-muted">The mobile number must be verified — an OTP goes to it before the login can be created.</small>
        }

        <div class="field">
            <label>Email <span class="req">*</span></label>
            <input pInputText type="email" formControlName="email" placeholder="name@company.com" autocomplete="off">
            @if (form.get('email')?.errors?.['taken']) {
            <small class="err">This email is already used by another login</small>
            } @else if (form.get('email')?.touched && form.get('email')?.invalid) {
            <small class="err">Valid email required</small>
            }
        </div>

        <div class="grid-2">
            <div class="field">
                <label>Password <span class="req">*</span></label>
                <div class="pw-wrap">
                    <input pInputText [type]="showPassword() ? 'text' : 'password'" formControlName="password"
                        placeholder="Min 8 characters" autocomplete="new-password">
                    <button type="button" class="pw-eye" (click)="showPassword.set(!showPassword())"
                        [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'">
                        <i class="pi" [class.pi-eye]="!showPassword()" [class.pi-eye-slash]="showPassword()"></i>
                    </button>
                </div>
                @if (form.get('password')?.touched && form.get('password')?.invalid) {
                <small class="err">At least 8 characters</small>
                }
            </div>
            <div class="field">
                <label>Confirm Password <span class="req">*</span></label>
                <input pInputText [type]="showPassword() ? 'text' : 'password'" formControlName="confirmPassword"
                    placeholder="Repeat password" autocomplete="new-password">
                @if (form.errors?.['passwordMismatch'] && form.get('confirmPassword')?.touched) {
                <small class="err">Passwords do not match</small>
                }
            </div>
        </div>

        <div class="modal-actions">
            <p-button label="Cancel" [outlined]="true" (onClick)="closeCreate()" />
            <p-button label="Create Login" (onClick)="save()" [loading]="saving()" />
        </div>
    </form>
</p-dialog>

<!-- ===================== RESET PASSWORD ===================== -->
<p-dialog [visible]="showReset()" (visibleChange)="showReset.set($event)" [modal]="true" [draggable]="true"
    [resizable]="false" [closable]="true" [dismissableMask]="false"
    [header]="'Reset Password — ' + (resetTarget()?.username || '')"
    styleClass="menu-dialog" [style]="{ width: '440px' }" [breakpoints]="{ '440px': '94vw' }">
    <form [formGroup]="resetForm" class="menu-form">
        <p class="hint-muted">
            The user's current sessions end immediately — they must log in with the new password.
        </p>

        <div class="field">
            <label>New Password <span class="req">*</span></label>
            <div class="pw-wrap">
                <input pInputText [type]="showResetPassword() ? 'text' : 'password'" formControlName="newPassword"
                    placeholder="Min 8 characters" autocomplete="new-password">
                <button type="button" class="pw-eye" (click)="showResetPassword.set(!showResetPassword())"
                    [attr.aria-label]="showResetPassword() ? 'Hide password' : 'Show password'">
                    <i class="pi" [class.pi-eye]="!showResetPassword()" [class.pi-eye-slash]="showResetPassword()"></i>
                </button>
            </div>
            @if (resetForm.get('newPassword')?.touched && resetForm.get('newPassword')?.invalid) {
            <small class="err">At least 8 characters</small>
            }
        </div>

        <div class="field">
            <label>Confirm Password <span class="req">*</span></label>
            <input pInputText [type]="showResetPassword() ? 'text' : 'password'" formControlName="confirmPassword"
                placeholder="Repeat password" autocomplete="new-password">
            @if (resetForm.errors?.['passwordMismatch'] && resetForm.get('confirmPassword')?.touched) {
            <small class="err">Passwords do not match</small>
            }
        </div>

        <div class="modal-actions">
            <p-button label="Cancel" [outlined]="true" (onClick)="closeReset()" />
            <p-button label="Reset Password" (onClick)="saveReset()" [loading]="resetting()" />
        </div>
    </form>
</p-dialog>
